// src/config/axiosConfig.js
import axios from "axios";
import ENV from "../config/env";

// ========= Ajusta si tu PHP está en subcarpeta =========
const SYNC_REFRESH_URL = `${ENV.PHP_SORT_SERVIDOR}/files/sync_refresh.php`;

// --- Helper: sincroniza refresh_token a la sesión PHP (para iframes/tablets)
async function syncRefreshToPhp(refreshToken) {
  if (!refreshToken) return;
  try {
    if (!SYNC_REFRESH_URL) return;
    await fetch(SYNC_REFRESH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // No bloquees flujo por fallo de sync
  }
}

// --- Helpers de tokens en storage ---
function getAccessToken() {
  return localStorage.getItem("token") || null;
}
function getRefreshToken() {
  return localStorage.getItem("refreshToken") || null;
}
function setTokens({ access, refresh }) {
  if (access) localStorage.setItem("token", access);
  if (refresh) localStorage.setItem("refreshToken", refresh);
}
function clearTokens() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
}

function attachAuthorization(config, token) {
  if (!config.headers) config.headers = {};
  config.headers.Authorization = `Bearer ${token}`;
  return config;
}

function forceLogout() {
  clearTokens();
  // Si ya estamos en /login no recargamos: una recarga aquí borra el
  // estado de la página (p. ej. el mensaje de error del formulario).
  if (window.location.pathname === "/login") return;
  // Si usas React Router, idealmente aquí disparas un navigate("/login")
  window.location.href = "/login";
}

// ================== Axios instances ==================

// Instancia principal (con interceptores)
const instance = axios.create({
  baseURL: ENV.API_BASE_URL,
});

// Instancia “limpia” SOLO para refresh (sin interceptores)
const refreshClient = axios.create({
  baseURL: ENV.API_BASE_URL,
});

// Flag para evitar bucles de refresh y reintentos
let isRefreshing = false;
/** @type {Array<{resolve:(access:string)=>void,reject:(err:any)=>void}>} */
let pendingRequests = [];

// --- Llama al endpoint de refresh y actualiza storage ---
async function doRefresh(currentRefreshToken) {
  // IMPORTANTE: usar refreshClient (sin interceptores)
  const resp = await refreshClient.post("/api/auth/refresh", {
    refresh_token: currentRefreshToken,
  });

  const newAccess =
    resp?.data?.token || resp?.data?.access_token || resp?.data?.accessToken;
  const newRefresh =
    resp?.data?.refresh_token || resp?.data?.refreshToken || null;

  if (!newAccess) throw new Error("El refresh no devolvió access token.");

  setTokens({ access: newAccess, refresh: newRefresh ?? currentRefreshToken });
  await syncRefreshToPhp(newRefresh ?? currentRefreshToken);
  return newAccess;
}

// --- Interceptor de REQUEST ---
instance.interceptors.request.use(
  async (config) => {
    // Si por alguna razón alguien marca esto, no tocamos auth
    if (config?.skipAuth === true) return config;

    const access = getAccessToken();
    const refresh = getRefreshToken();

    if (access) return attachAuthorization(config, access);

    // Si no hay access pero hay refresh, hacemos refresh PROACTIVO
    if (refresh) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve: (newAccess) =>
              resolve(attachAuthorization(config, newAccess)),
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        const newAccess = await doRefresh(refresh);

        const queue = pendingRequests.splice(0, pendingRequests.length);
        queue.forEach(({ resolve }) => resolve(newAccess));

        return attachAuthorization(config, newAccess);
      } catch (err) {
        const queue = pendingRequests.splice(0, pendingRequests.length);
        queue.forEach(({ reject }) => reject(err));

        forceLogout();
        throw err;
      } finally {
        isRefreshing = false;
      }
    }

    // Sin tokens → request público
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Interceptor de RESPONSE ---
instance.interceptors.response.use(
  (response) => {
    // Aplanar el nuevo formato estándar { success, message, data }
    // para que el resto del código siga leyendo response.data sin cambios
    const body = response.data;
    if (
      body &&
      typeof body === "object" &&
      "success" in body &&
      "data" in body
    ) {
      response.data = body.data;
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    if (!originalRequest) return Promise.reject(error);

    // Si el 401/403 viene del endpoint de refresh → no reintentar nunca
    const url = String(originalRequest?.url || "");
    if ((status === 401 || status === 403) && url.includes("/api/auth/refresh")) {
      forceLogout();
      return Promise.reject(error);
    }

    // Un 401 del login significa credenciales inválidas, no token vencido:
    // propagar el error tal cual para que la UI muestre el mensaje,
    // sin intentar refresh ni redirigir (la redirección recargaba la
    // página y hacía desaparecer el mensaje de error).
    if (url.includes("/api/auth/login")) {
      return Promise.reject(error);
    }

    // Ya reintentado → propaga
    if (originalRequest._retry) return Promise.reject(error);

    if (status === 401 || status === 403) {
      const refresh = getRefreshToken();
      if (!refresh) {
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve: async (newAccess) => {
              try {
                originalRequest._retry = true;
                resolve(await instance(attachAuthorization(originalRequest, newAccess)));
              } catch (e) {
                reject(e);
              }
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        const newAccess = await doRefresh(refresh);

        const queue = pendingRequests.splice(0, pendingRequests.length);
        queue.forEach(({ resolve }) => resolve(newAccess));

        originalRequest._retry = true;
        return instance(attachAuthorization(originalRequest, newAccess));
      } catch (err) {
        const queue = pendingRequests.splice(0, pendingRequests.length);
        queue.forEach(({ reject }) => reject(err));

        forceLogout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default instance;